import { HomeIcon, MapPinIcon, ReportIcon, SettingsIcon, TimerIcon } from '../icons';
import { LccRole } from '../auth/types';
import { ModuleDefinition } from './modules';

/**
 * Registro de pantallas de TurnoGeo - mismo patron que shared/config/modules.ts
 * (Control Operativo) y shared/config/hrModules.ts (Gestion Humana): cada
 * aplicacion de la Suite tiene su propio catalogo de pantallas, su propio
 * set de Admin-only y su propio default por Rol (el Rol es POR APLICACION).
 *
 * Fase 1 (este entregable): Inicio, Marcar (entrada/salida), Mis Registros y
 * Configuracion (solo Admin).
 */
export const TURNOGEO_MODULES: ModuleDefinition[] = [
  { id: 'turnogeo-home', label: 'Inicio', path: '/turno-geo', icon: HomeIcon, inBottomNav: true, status: 'active' },
  { id: 'turnogeo-mark', label: 'Marcar', path: '/turno-geo/marcar', icon: MapPinIcon, inBottomNav: true, status: 'active' },
  { id: 'turnogeo-records', label: 'Mis Registros', path: '/turno-geo/mis-registros', icon: TimerIcon, inBottomNav: true, status: 'active' },
  { id: 'turnogeo-novelties', label: 'Novedades', path: '/turno-geo/novedades', icon: ReportIcon, inBottomNav: true, status: 'active' },
  { id: 'turnogeo-hours', label: 'Horas y recargos', path: '/turno-geo/horas', icon: TimerIcon, inBottomNav: false, status: 'active' },
  { id: 'turnogeo-overtime', label: 'Horas extras', path: '/turno-geo/horas-extras', icon: TimerIcon, inBottomNav: false, status: 'active' },
  { id: 'turnogeo-register', label: 'Registrador', path: '/turno-geo/registrador', icon: MapPinIcon, inBottomNav: false, status: 'active' },
  { id: 'turnogeo-settings', label: 'Configuración', path: '/turno-geo/configuracion', icon: SettingsIcon, inBottomNav: false, status: 'active' },
];

/** Ids de "pantalla" asignables individualmente por usuario, dentro de TurnoGeo. */
export const TURNOGEO_ASSIGNABLE_MODULE_IDS = ['turnogeo-mark', 'turnogeo-records', 'turnogeo-hours', 'turnogeo-overtime', 'turnogeo-register', 'turnogeo-settings'] as const;
export type TurnoGeoAssignableModuleId = (typeof TURNOGEO_ASSIGNABLE_MODULE_IDS)[number];

export const TURNOGEO_ASSIGNABLE_MODULES = TURNOGEO_MODULES.filter((m) => (TURNOGEO_ASSIGNABLE_MODULE_IDS as readonly string[]).includes(m.id));

/**
 * Pantallas que solo puede tener un líder (Admin o Supervisor): un Operador nunca las ve, ni con override individual.
 * "Acceso y usuarios", "Empleados" y "Factores de recargo" (dentro de Configuración) son solo del Administrador.
 */
export const TURNOGEO_LEADER_ONLY_MODULE_IDS = ['turnogeo-hours', 'turnogeo-overtime', 'turnogeo-settings'] as const;
export const TURNOGEO_ADMIN_ONLY_MODULE_IDS = [] as const;

/** Pantallas por defecto de cada rol EN TurnoGeo (independientes de sus defaults en otras aplicaciones). */
export const TURNOGEO_ROLE_DEFAULT_MODULES: Record<LccRole, TurnoGeoAssignableModuleId[]> = {
  admin: ['turnogeo-mark', 'turnogeo-records', 'turnogeo-hours', 'turnogeo-overtime', 'turnogeo-register', 'turnogeo-settings'],
  supervisor: ['turnogeo-mark', 'turnogeo-records', 'turnogeo-hours', 'turnogeo-overtime', 'turnogeo-register', 'turnogeo-settings'],
  operator: ['turnogeo-mark', 'turnogeo-records', 'turnogeo-register'],
};

export function resolveEffectiveTurnoGeoModules(role: LccRole, allowedModules: string[] | null): string[] {
  if (role === 'admin') return TURNOGEO_ROLE_DEFAULT_MODULES.admin;
  const base = allowedModules && allowedModules.length > 0 ? allowedModules : TURNOGEO_ROLE_DEFAULT_MODULES[role];
  return role === 'supervisor' ? base : base.filter((id) => !(TURNOGEO_LEADER_ONLY_MODULE_IDS as readonly string[]).includes(id));
}

export function visibleTurnoGeoModules(all: ModuleDefinition[], effective: string[], role: LccRole): ModuleDefinition[] {
  return all.filter((m) => {
    if ((TURNOGEO_ADMIN_ONLY_MODULE_IDS as readonly string[]).includes(m.id)) return role === 'admin';
    if ((TURNOGEO_ASSIGNABLE_MODULE_IDS as readonly string[]).includes(m.id)) return effective.includes(m.id);
    return true;
  });
}

export function getVisibleTurnoGeoModules(role: LccRole, allowedModules: string[] | null): ModuleDefinition[] {
  return visibleTurnoGeoModules(TURNOGEO_MODULES, resolveEffectiveTurnoGeoModules(role, allowedModules), role);
}
